/**
 * ERC-8004 Agent Explorer Page
 * Main entry point for discovering and executing ERC-8004 agents
 */

import { useState } from 'react';
import { Search, Network, Loader2, Bot, ArrowLeft, AlertTriangle } from 'lucide-react';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useSignTypedData, useSwitchChain } from 'wagmi';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ErrorMessage } from '@/components/ErrorMessage';
import { ExecutionPlanDisplay } from '@/components/ExecutionPlanDisplay';
import { ExecutionResultsDisplay } from '@/components/ExecutionResultsDisplay';
import { API_URL } from '@/lib/api';
import { CHAIN_INFO, type ChainId } from '@/config/wagmi';

// USDC Contract addresses per network (for EIP-3409 TransferWithAuthorization)
const USDC_ADDRESSES: Record<number, string> = {
  8453: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',   // Base mainnet
  84532: '0x036CbD53842c5426634e7929541eC2318f3dCF7e',  // Base Sepolia testnet
  1: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48',     // Ethereum mainnet
  11155111: '0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238', // Sepolia testnet
};

type ViewState = 'search' | 'plan' | 'executing' | 'results';

interface ExecutionPlanResponse {
  query: string;
  query_id: string;
  capability_analysis: {
    primary_capabilities: string[];
    secondary_capabilities: string[];
    query_type: string;
    all_matches: Array<{
      capability: string;
      score: number;
    }>;
  };
  execution_plans: Array<{
    plan_id: string;
    query: string;
    strategy: string;
    steps: Array<{
      step_id: number;
      agent_id: string;
      agent_name: string;
      task: string;
      capability: string;
      cost_usdc: number;
      reputation_score: number;
      estimated_duration_sec: number;
      endpoint: string;
      agent_wallet_address: string;
      wallet_chain_id?: number; // Chain ID for payment (e.g., 84532 for Base Sepolia)
      api_endpoint?: string; // Alternative name for endpoint
      depends_on: number[];
    }>;
    total_cost_usdc: number;
    total_duration_sec: number;
    confidence_score: number;
  }>;
  recommended_plan: any;
  payment_details: {
    method: string;
    deep42_fee_usdc: number;
    user_approval_required: boolean;
    note?: string;
  };
  dry_run: string;
}

export function Erc8004ExplorerPage() {
  const { address, isConnected, chain } = useAccount();
  const { signTypedDataAsync } = useSignTypedData();
  const { switchChainAsync } = useSwitchChain();
  const [searchQuery, setSearchQuery] = useState('');
  const [maxBudget, setMaxBudget] = useState(0.30);
  const [strategy, setStrategy] = useState<'greedy' | 'balanced' | 'quality'>('balanced');
  const [chainFilter, setChainFilter] = useState<'all' | '84532' | '11155111'>('all');

  const [viewState, setViewState] = useState<ViewState>('search');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [executionPlan, setExecutionPlan] = useState<ExecutionPlanResponse | null>(null);
  const [executionResults, setExecutionResults] = useState<any | null>(null);
  const [executionProgress, setExecutionProgress] = useState<{
    currentStep: number;
    totalSteps: number;
    status: string;
  }>({ currentStep: 0, totalSteps: 0, status: '' });

  const exampleQueries = [
    'what is the latest block on solana?',
    'what is the 24h volume of TRUMP on solana?',
    'what are the top tokens held for vitalik.eth?',
    'analyze the TVL on Uniswap V3',
    'get the current ETH gas price',
  ];

  const handleGeneratePlan = async () => {
    if (!searchQuery.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/erc8004/execute-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: searchQuery,
          max_budget: maxBudget,
          strategy,
          chain_filter: chainFilter === 'all' ? null : parseInt(chainFilter),
          dry_run: true, // Generate plan only
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate plan: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to generate execution plan');
      }

      setExecutionPlan(result.data);
      setViewState('plan');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('[Generate Plan Error]', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExecutePlan = async () => {
    if (!executionPlan) return;

    setError(null);

    // Check if any steps require payment (not free agents)
    // We'll check this during execution per step

    try {
      const steps = executionPlan.recommended_plan.steps;
      console.log('[Execute] Starting execution with', steps.length, 'steps');

      setViewState('executing');
      const stepResults = [];

      // Execute each step one by one
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];

        setExecutionProgress({
          currentStep: i + 1,
          totalSteps: steps.length,
          status: `Preparing step ${i + 1}: ${step.task}`
        });

        console.log(`[Execute] Starting step ${step.step_id}: ${step.task}`);

        // Step 1: Prepare - get payment request from agent
        console.log('[Execute] Preparing step...');
        // Use api_endpoint if available, otherwise fall back to endpoint
        const agentEndpoint = step.api_endpoint || step.endpoint;
        console.log('[Execute] Using agent endpoint:', agentEndpoint);

        const prepareResponse = await fetch(`${API_URL}/api/erc8004/prepare-step`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            agentEndpoint,
            query: executionPlan.query,
            capability: step.capability
          })
        });

        if (!prepareResponse.ok) {
          const errorText = await prepareResponse.text();
          throw new Error(`Failed to prepare step ${step.step_id}: ${errorText}`);
        }

        const prepareResult = await prepareResponse.json();
        if (!prepareResult.success) {
          throw new Error(prepareResult.error || 'Preparation failed');
        }

        const { paymentId, nonce, paymentRequest, taskId, resource, isGateway, isFreeAgent, isA2AAgent } = prepareResult.data;

        // Handle free agents (no payment required)
        if (isFreeAgent) {
          console.log('[Execute] Free agent detected - skipping payment');

          setExecutionProgress({
            currentStep: i + 1,
            totalSteps: steps.length,
            status: `Executing free agent for step ${i + 1}...`
          });

          const executeResponse = await fetch(`${API_URL}/api/erc8004/execute-step`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              agentEndpoint,
              query: executionPlan.query,
              capability: step.capability,
              isFreeAgent: true,
              isA2AAgent: true
            })
          });

          if (!executeResponse.ok) {
            const errorText = await executeResponse.text();
            throw new Error(`Failed to execute free agent step ${step.step_id}: ${errorText}`);
          }

          const executeResult = await executeResponse.json();
          console.log('[Execute] Free agent result:', JSON.stringify(executeResult, null, 2));

          if (!executeResult.success) {
            throw new Error(executeResult.error || 'Execution failed');
          }

          if (!executeResult.data || !executeResult.data.result) {
            console.error('[Execute] Missing result in response:', executeResult);
            throw new Error('No result data received from free agent');
          }

          console.log('[Execute] Free agent result data:', {
            result: executeResult.data.result,
            resultType: typeof executeResult.data.result,
            resultLength: executeResult.data.result?.length
          });

          stepResults.push({
            stepId: step.step_id,
            agent: step.agent_name,
            capability: step.capability,
            result: executeResult.data.result,
            cost: 0 // Free agent
          });

          console.log(`[Execute] Step ${step.step_id} completed successfully (free agent)`);
          continue; // Skip to next step
        }

        // PAID AGENT - Requires wallet connection and payment
        if (!isConnected || !address) {
          throw new Error('Please connect your wallet to execute paid agents');
        }

        // Map network name to chain ID (use actual payment network, not execution plan's chain)
        const networkToChainId: Record<string, number> = {
          'base': 8453,              // Base mainnet
          'baseSepolia': 84532,      // Base Sepolia testnet
          'base-sepolia': 84532,     // Alternative format
          'ethereum': 1,             // Ethereum mainnet
          'sepolia': 11155111,       // Sepolia testnet
          'arbitrum': 42161,         // Arbitrum One
          'polygon': 137,            // Polygon mainnet
        };

        const actualChainId = networkToChainId[paymentRequest.network] || requiredChainId;
        console.log('[Execute] Payment network:', paymentRequest.network, '→ Chain ID:', actualChainId);

        // Check if we need to switch networks (use actual payment network, not execution plan)
        if (chain?.id !== actualChainId) {
          const networkInfo = CHAIN_INFO[actualChainId as ChainId];
          console.log(`[Execute] Switching from ${chain?.id} to ${networkInfo?.name} (${actualChainId}) for payment...`);

          try {
            await switchChainAsync({ chainId: actualChainId });
            console.log(`[Execute] Switched to ${networkInfo?.name} successfully`);
          } catch (switchErr: any) {
            throw new Error(`Failed to switch to ${networkInfo?.name}. Please switch manually and try again.`);
          }
        }

        // Step 2: Sign EIP-3409 TransferWithAuthorization (gasless USDC transfer)
        setExecutionProgress({
          currentStep: i + 1,
          totalSteps: steps.length,
          status: `Signing payment authorization for step ${i + 1}...`
        });

        // Use the actual chain ID from payment network
        const stepChainId = actualChainId;

        // Get USDC contract address for the payment network
        const usdcAddress = USDC_ADDRESSES[stepChainId];
        if (!usdcAddress) {
          throw new Error(`USDC contract not configured for chain ${stepChainId}`);
        }

        // Get USDC contract name and version from payment extra data
        const usdcName = paymentRequest.extra?.name || 'USD Coin';
        const usdcVersion = paymentRequest.extra?.version || '2';

        // Generate cryptographically secure nonce (32 bytes)
        const nonceBytes = new Uint8Array(32);
        crypto.getRandomValues(nonceBytes);
        const authNonce = '0x' + Array.from(nonceBytes)
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');

        const validBefore = Math.floor(Date.now() / 1000) + 600; // 10 minutes from now

        // Create authorization object for EIP-3409
        const authorization = {
          from: address,
          to: paymentRequest.to,
          value: paymentRequest.amount,
          validAfter: '0',
          validBefore: validBefore.toString(),
          nonce: authNonce
        };

        console.log('[Execute] Signing EIP-3409 TransferWithAuthorization...');
        console.log('[Execute] USDC contract:', usdcAddress);
        console.log('[Execute] USDC domain:', { name: usdcName, version: usdcVersion, chainId: stepChainId });
        console.log('[Execute] Authorization:', authorization);

        // Sign against USDC contract's EIP-712 domain (NOT custom x402 domain!)
        const signature = await signTypedDataAsync({
          domain: {
            name: usdcName,
            version: usdcVersion,
            chainId: stepChainId,
            verifyingContract: usdcAddress as `0x${string}`
          },
          types: {
            TransferWithAuthorization: [
              { name: 'from', type: 'address' },
              { name: 'to', type: 'address' },
              { name: 'value', type: 'uint256' },
              { name: 'validAfter', type: 'uint256' },
              { name: 'validBefore', type: 'uint256' },
              { name: 'nonce', type: 'bytes32' }
            ]
          },
          primaryType: 'TransferWithAuthorization',
          message: {
            from: address as `0x${string}`,
            to: paymentRequest.to as `0x${string}`,
            value: BigInt(paymentRequest.amount),
            validAfter: BigInt(0),
            validBefore: BigInt(validBefore),
            nonce: authNonce as `0x${string}`
          }
        });

        console.log('[Execute] EIP-3409 signature obtained:', signature);

        // Step 3: Submit payment authorization to gateway (NO on-chain transaction!)
        // The gateway will execute the gasless transfer on our behalf
        setExecutionProgress({
          currentStep: i + 1,
          totalSteps: steps.length,
          status: `Submitting payment and executing agent for step ${i + 1}...`
        });

        console.log('[Execute] Submitting payment authorization to gateway (no on-chain tx)...');
        const paymentPayload = {
          signature,
          authorization,
          network: paymentRequest.network
        };

        const executeResponse = await fetch(`${API_URL}/api/erc8004/execute-step`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            agentEndpoint,
            query: executionPlan.query,
            capability: step.capability,
            paymentId,
            paymentPayload,
            taskId,
            resource,
            isGateway
          })
        });

        if (!executeResponse.ok) {
          const errorText = await executeResponse.text();
          throw new Error(`Failed to execute step ${step.step_id}: ${errorText}`);
        }

        const executeResult = await executeResponse.json();
        console.log('[Execute] Execute result:', JSON.stringify(executeResult, null, 2));

        if (!executeResult.success) {
          throw new Error(executeResult.error || 'Execution failed');
        }

        if (!executeResult.data || !executeResult.data.result) {
          console.error('[Execute] Missing result in response:', executeResult);
          throw new Error('No result data received from agent');
        }

        stepResults.push({
          stepId: step.step_id,
          agent: step.agent_name,
          capability: step.capability,
          result: executeResult.data.result,
          cost: step.cost_usdc
        });

        console.log(`[Execute] Step ${step.step_id} completed successfully`);
      }

      // All steps complete! Transform to format expected by ExecutionResultsDisplay
      console.log('[Execute] Building execution results from stepResults:', stepResults);

      const formattedResponses = stepResults.map((step, index) => {
        const responseText = typeof step.result === 'string' ? step.result : JSON.stringify(step.result, null, 2);
        console.log(`[Execute] Response ${index}:`, {
          stepId: step.stepId,
          agent: step.agent,
          resultType: typeof step.result,
          resultPreview: step.result?.substring?.(0, 100),
          responseTextLength: responseText?.length
        });

        return {
          step_id: step.stepId || index,
          agent_id: `agent-${index}`,
          agent_name: step.agent,
          capability: step.capability,
          response_text: responseText,
          response_json: typeof step.result === 'object' ? step.result : null,
          success: true,
          response_time_ms: 0, // Not tracked per-step currently
          cost_usdc: step.cost
        };
      });

      console.log('[Execute] Formatted responses:', formattedResponses);

      setExecutionResults({
        query_id: executionPlan.query,
        plan_id: executionPlan.recommended_plan.plan_id,
        query: executionPlan.query,
        status: 'completed',
        total_cost_usdc: executionPlan.recommended_plan.total_cost_usdc,
        total_duration_ms: executionPlan.recommended_plan.total_duration_sec * 1000,
        responses: formattedResponses
      });
      setViewState('results');
    } catch (err) {
      console.error('[Execute Plan Error]', err);

      // ✅ FIX #6: Better error messages
      let userMessage = 'Execution failed';

      if (err instanceof Error) {
        const errMsg = err.message.toLowerCase();

        if (errMsg.includes('user rejected') || errMsg.includes('user denied')) {
          userMessage = 'You rejected the wallet transaction. Click Execute to try again.';
        } else if (errMsg.includes('insufficient funds') || errMsg.includes('insufficient balance')) {
          userMessage = 'Insufficient USDC or ETH for gas fees. Please add funds to your wallet on Base network.';
        } else if (errMsg.includes('timeout') || errMsg.includes('timed out')) {
          userMessage = 'Request timed out. The agent might be busy, please try again in a moment.';
        } else if (errMsg.includes('402') || errMsg.includes('payment required')) {
          userMessage = 'Payment request failed. The agent might not support x402 payments or is temporarily unavailable.';
        } else if (errMsg.includes('network') || errMsg.includes('switch')) {
          userMessage = err.message;
        } else if (errMsg.includes('transaction failed')) {
          userMessage = err.message;
        } else {
          userMessage = `Execution failed: ${err.message}`;
        }
      }

      setError(userMessage);
      setViewState('plan'); // Go back to plan view on error
    }
  };

  const handleCancelPlan = () => {
    setExecutionPlan(null);
    setViewState('search');
  };

  const handleNewQuery = () => {
    setSearchQuery('');
    setExecutionPlan(null);
    setExecutionResults(null);
    setViewState('search');
    setError(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleGeneratePlan();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Wallet Connection Section */}
        <div className="mb-4 flex items-center justify-end gap-4 text-sm">
          <ConnectButton />
        </div>
        {/* Back Button */}
        {(viewState === 'plan' || viewState === 'results') && (
          <button
            onClick={() => setViewState('search')}
            className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Search</span>
          </button>
        )}

        {/* Search View */}
        {viewState === 'search' && (
          <>
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="space-y-4">
                {/* Query Input */}
                <div>
                  <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                    What would you like to know?
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="search"
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="e.g., what is the latest block on solana?"
                      className="pl-10 w-full rounded-md border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 text-lg"
                    />
                  </div>
                </div>

                {/* Parameters */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-1">
                      Max Budget (USDC)
                    </label>
                    <input
                      id="budget"
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={maxBudget}
                      onChange={(e) => setMaxBudget(parseFloat(e.target.value))}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="strategy" className="block text-sm font-medium text-gray-700 mb-1">
                      Strategy
                    </label>
                    <select
                      id="strategy"
                      value={strategy}
                      onChange={(e) => setStrategy(e.target.value as 'greedy' | 'balanced' | 'quality')}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="greedy">Greedy (Lowest Cost)</option>
                      <option value="balanced">Balanced</option>
                      <option value="quality">Quality (Best Agents)</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="chain" className="block text-sm font-medium text-gray-700 mb-1">
                      Chain Filter
                    </label>
                    <select
                      id="chain"
                      value={chainFilter}
                      onChange={(e) => setChainFilter(e.target.value as 'all' | '84532' | '11155111')}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="all">All Chains</option>
                      <option value="84532">Base Sepolia</option>
                      <option value="11155111">Ethereum Sepolia</option>
                    </select>
                  </div>

                  <div className="flex items-end">
                    <button
                      onClick={handleGeneratePlan}
                      disabled={isLoading || !searchQuery.trim()}
                      className="w-full px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Generating Plan...
                        </>
                      ) : (
                        <>
                          <Network className="h-4 w-4" />
                          Generate Plan
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Example Queries */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-3">Try these examples:</p>
                <div className="flex flex-wrap gap-2">
                  {exampleQueries.map((query, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSearchQuery(query)}
                      className="text-sm px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-700 transition-colors"
                    >
                      {query}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="mb-6">
                <ErrorMessage error={new Error(error)} />
              </div>
            )}

            {/* Empty State */}
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              <Bot className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Execute Queries with AI Agents
              </h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Enter a query above to generate an execution plan using specialized ERC-8004 agents.
                Review the plan, costs, and execution steps before executing.
              </p>
            </div>
          </>
        )}

        {/* Plan View */}
        {viewState === 'plan' && executionPlan && (
          <ExecutionPlanDisplay
            query={executionPlan.query}
            queryId={executionPlan.query_id}
            capabilityAnalysis={executionPlan.capability_analysis}
            executionPlans={executionPlan.execution_plans}
            recommendedPlan={executionPlan.recommended_plan}
            paymentDetails={executionPlan.payment_details}
            onExecute={handleExecutePlan}
            onCancel={handleCancelPlan}
            currentChainId={chain?.id}
          />
        )}

        {/* Executing View */}
        {viewState === 'executing' && (
          <div className="bg-white rounded-lg shadow-sm p-12">
            <div className="text-center mb-6">
              <LoadingSpinner />
              <h3 className="text-lg font-medium text-gray-900 mt-6 mb-2">
                Executing Step {executionProgress.currentStep} of {executionProgress.totalSteps}
              </h3>
              <p className="text-gray-600 max-w-md mx-auto mb-4">
                {executionProgress.status || 'Calling agents and waiting for responses...'}
              </p>
            </div>

            {/* Progress bar */}
            {executionProgress.totalSteps > 0 && (
              <div className="max-w-md mx-auto">
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div
                    className="bg-purple-600 h-2 rounded-full transition-all duration-500"
                    style={{
                      width: `${(executionProgress.currentStep / executionProgress.totalSteps) * 100}%`
                    }}
                  />
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Progress</span>
                  <span>{Math.round((executionProgress.currentStep / executionProgress.totalSteps) * 100)}%</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Results View */}
        {viewState === 'results' && executionResults && (
          <ExecutionResultsDisplay
            result={executionResults}
            onClose={() => setViewState('search')}
            onNewQuery={handleNewQuery}
          />
        )}
      </div>
    </div>
  );
}
